import { InfoCard, ProcessLoader } from '@builder';
import { FC, useEffect, useState } from 'react';
import { StyledLoaderContainer, infoCardStyles } from './Scheduler.styles';
import { useTranslation } from 'react-i18next';
import { CALENDAR_MONTH_FORMAT, GENETIC_COUNSELING_TRANSLATION } from '../../constants';
import SchedulingBackgroundForm from '../SchedulingBackgroundForm/SchedulingBackgroundForm';
import AppointmentScheduler from '../AppointmentScheduler/AppointmentScheduler';
import { SchedulingBackgroundFormFormValues } from '../SchedulingBackgroundForm/SchedulingBackgroundForm.types';
import useQueryAvailableDates from '@modules/genetic-counseling/hooks/useQueryAvailableDates';
import { formatDate } from '@/common/utils';
import { Notification, NotificationAlignment, NotificationVariants } from '@base-app';
import { HELIX_SUPPORT_EMAIL } from '@/common/constants';
import { SchedulerProps } from './Scheduler.types';

const Scheduler: FC<SchedulerProps> = ({ isReschedulerFlow = false }) => {
  const { t } = useTranslation(GENETIC_COUNSELING_TRANSLATION);
  const [stateCode, setStateCode] = useState<string>();
  const [timeZone, setTimeZone] = useState<string>();
  const [language, setLanguage] = useState<string>();
  const [notesForAppointment, setNotesForAppointment] = useState<string>();
  const [areDatesFetchedFirstTime, setAreDatesFetchedFirstTime] = useState<boolean>(false);
  const [showCalendar, setShowCalendar] = useState<boolean>(false);
  const [month, setMonth] = useState(formatDate(new Date(), CALENDAR_MONTH_FORMAT));

  const {
    data: datesData,
    refetch,
    isError,
    isFetching,
    remove,
  } = useQueryAvailableDates(
    {
      language: language ?? '',
      month,
      stateCode: stateCode ?? '',
      timeZone: timeZone ?? '',
    },
    {
      enabled: areDatesFetchedFirstTime,
    },
  );

  useEffect(() => {
    if (areDatesFetchedFirstTime) void refetch();
  }, [month]);

  useEffect(() => {
    if (datesData !== undefined) {
      setShowCalendar(true);
    }
  }, [datesData]);

  const handleBackButtonClick = (): void => {
    remove();
    setAreDatesFetchedFirstTime(false);
    setShowCalendar(false);
  };

  const handleFormSubmit = (values: SchedulingBackgroundFormFormValues): void => {
    setStateCode(values.state);
    setTimeZone(values.timezone);
    setLanguage(values.language);
    setNotesForAppointment(values.notesForAppointment);
    setAreDatesFetchedFirstTime(true);
    setMonth(formatDate(new Date(), CALENDAR_MONTH_FORMAT));
  };

  return (
    <InfoCard
      css={{
        marginTop: `${isReschedulerFlow ? '0' : '$6'}`,
        ...infoCardStyles,
      }}
    >
      {isError && (
        <Notification
          css={{ marginBottom: '$8' }}
          description={
            <>
              {t('scheduler.dates_error_notification.description')}
              <a href={`mailto: ${HELIX_SUPPORT_EMAIL}`}>{HELIX_SUPPORT_EMAIL}</a>
            </>
          }
          textAlign={NotificationAlignment.LEFT}
          title={t('scheduler.dates_error_notification.title')}
          variant={NotificationVariants.ERROR}
        />
      )}
      {!showCalendar && !isFetching && (
        <SchedulingBackgroundForm
          state={stateCode}
          timezoneId={timeZone}
          language={language}
          notesForAppointment={notesForAppointment}
          data-testid="scheduler-background"
          handleFormSubmit={handleFormSubmit}
        />
      )}
      {isFetching && !showCalendar && (
        <StyledLoaderContainer>
          <ProcessLoader
            data-testid="process-loader-apponintment-scheduler"
            title={t('scheduler.loading_appointment_scheduler')}
          />
        </StyledLoaderContainer>
      )}
      {showCalendar && stateCode !== undefined && timeZone !== undefined && language !== undefined && (
        <AppointmentScheduler
          availableDates={datesData?.availableDates}
          isFetchingDates={isFetching}
          isReschedulerFlow={isReschedulerFlow}
          data-testid="appointment-scheduler"
          handleBackButtonClick={handleBackButtonClick}
          handleMonthChange={setMonth}
          stateCode={stateCode}
          timeZone={timeZone}
          language={language}
          notesForAppointment={notesForAppointment}
        />
      )}
    </InfoCard>
  );
};

export default Scheduler;
